import { useState, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Upload, Trash2, Search, Image, Video, Grid, List, Send, X, AlertCircle } from 'lucide-react';
import { TopNav } from '@/components/layout/TopNav';
import { Button } from '@/components/ui/Button';
import { mediaApi, ApiError, MEDIA_ACCEPT, MEDIA_MAX_UPLOAD_BYTES, type MediaItem } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { formatRelativeTime, formatBytes, cn } from '@/lib/utils';

const PAGE_SIZE = 50;

export function Media() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Open the composer pre-loaded with this asset attached.
  const useInPost = (item: MediaItem) =>
    navigate(`/publishing?compose=true&media=${encodeURIComponent(item.r2_key)}`);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['media', currentWorkspace?.id, typeFilter, search],
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      return mediaApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        type: typeFilter || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    // A short page means we've reached the end; otherwise continue after
    // everything loaded so far.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.media.length < PAGE_SIZE
        ? undefined
        : allPages.reduce((n, p) => n + p.media.length, 0),
    enabled: !!currentWorkspace,
  });

  const deleteMedia = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) => {
      const token = await getToken();
      return mediaApi.delete(token!, id, force);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', currentWorkspace?.id] }),
  });

  const handleDelete = async (item: MediaItem) => {
    if (!window.confirm(`Delete "${item.filename}"? This permanently removes the file.`)) return;
    try {
      await deleteMedia.mutateAsync({ id: item.id });
    } catch (err) {
      // 409: posts still use this file — warn with the count and let the user force it.
      if (err instanceof ApiError && err.status === 409) {
        if (!window.confirm(`${err.message}\n\nDelete it anyway?`)) return;
        try {
          await deleteMedia.mutateAsync({ id: item.id, force: true });
        } catch (forceErr) {
          setErrors([`Couldn't delete ${item.filename}: ${forceErr instanceof Error ? forceErr.message : 'Delete failed'}`]);
        }
        return;
      }
      setErrors([`Couldn't delete ${item.filename}: ${err instanceof Error ? err.message : 'Delete failed'}`]);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !currentWorkspace) return;

    setUploading(true);
    setErrors([]);
    const token = await getToken();
    const failed: string[] = [];

    for (const file of files) {
      try {
        // Direct upload — sends the bytes to the Worker which stores them in R2.
        // (The old multipart upload-url/confirm flow never uploaded the bytes.)
        await mediaApi.upload(token!, currentWorkspace.id, file);
      } catch (err) {
        failed.push(`${file.name}: ${err instanceof Error ? err.message : 'Upload failed'}`);
      }
    }

    setErrors(failed);
    setUploading(false);
    qc.invalidateQueries({ queryKey: ['media', currentWorkspace?.id] });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const items = data?.pages.flatMap((p) => p.media) ?? [];

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <TopNav
        title="Media Library"
        action={{
          label: uploading ? 'Uploading...' : 'Upload',
          onClick: () => fileInputRef.current?.click(),
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={MEDIA_ACCEPT}
        className="hidden"
        onChange={handleFileUpload}
      />

      <div className="p-6 space-y-4 flex-1 overflow-auto mx-auto w-full max-w-[1400px]">
        {/* Toolbar */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="flex gap-1">
            {['', 'image', 'video'].map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md transition-colors',
                  typeFilter === t
                    ? 'bg-accent/10 text-accent'
                    : 'text-muted-foreground hover:bg-muted/40'
                )}
              >
                {t === '' && 'All'}
                {t === 'image' && <><Image className="h-3 w-3" /> Images</>}
                {t === 'video' && <><Video className="h-3 w-3" /> Videos</>}
              </button>
            ))}
          </div>
          <div className="flex gap-1 ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              className={cn('p-1.5 rounded-md', viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/40')}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn('p-1.5 rounded-md', viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/40')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="flex items-start gap-2 rounded-[10px] px-3 py-2.5 bg-destructive/10 text-destructive ring-1 ring-destructive/30 text-xs">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <ul className="flex-1 space-y-0.5">
              {errors.map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
            <button onClick={() => setErrors([])} className="shrink-0 hover:opacity-70" aria-label="Dismiss error">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Upload dropzone */}
        {items.length === 0 && !isLoading && (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-accent/50 transition-colors"
          >
            <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">Drop files here or click to upload</p>
            <p className="text-xs text-muted-foreground">
              Supports JPEG, PNG, GIF, WebP, MP4, MOV, WebM · up to {MEDIA_MAX_UPLOAD_BYTES / 1024 / 1024} MB
            </p>
          </div>
        )}

        {/* Media grid/list */}
        {isLoading ? (
          <div className={cn('gap-3', viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' : 'space-y-2')}>
            {[...Array(10)].map((_, i) => (
              <div key={i} className={cn('bg-muted rounded-lg animate-pulse', viewMode === 'grid' ? 'aspect-square' : 'h-14')} />
            ))}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {items.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                onDelete={() => handleDelete(item)}
                onOpen={() => setPreview(item)}
                onUse={() => useInPost(item)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <MediaRow
                key={item.id}
                item={item}
                onDelete={() => handleDelete(item)}
                onOpen={() => setPreview(item)}
                onUse={() => useInPost(item)}
              />
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="flex justify-center pt-2">
            <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
              Load more
            </Button>
          </div>
        )}
      </div>

      {preview && (
        <MediaPreview
          item={preview}
          onClose={() => setPreview(null)}
          onUse={() => {
            const item = preview;
            setPreview(null);
            useInPost(item);
          }}
        />
      )}
    </div>
  );
}

function MediaPreview({
  item,
  onClose,
  onUse,
}: {
  item: MediaItem;
  onClose: () => void;
  onUse: () => void;
}) {
  const isImage = item.mime_type?.startsWith('image/');
  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2 gap-3">
          <p className="text-sm text-white/90 truncate">{item.filename}</p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onUse}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              Use in post
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-white/70 hover:text-white rounded-md hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {isImage ? (
          <img
            src={mediaApi.serveUrl(item.r2_key)}
            alt={item.filename}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
          />
        ) : (
          <video
            src={mediaApi.serveUrl(item.r2_key)}
            controls
            autoPlay
            className="max-w-full max-h-[80vh] rounded-lg bg-black"
          />
        )}
      </div>
    </div>
  );
}

function MediaCard({
  item,
  onDelete,
  onOpen,
  onUse,
}: {
  item: MediaItem;
  onDelete: () => void;
  onOpen: () => void;
  onUse: () => void;
}) {
  const isImage = item.mime_type?.startsWith('image/');
  return (
    <div
      onClick={onOpen}
      className="group relative bg-card border border-border rounded-lg overflow-hidden aspect-square cursor-pointer"
    >
      {isImage ? (
        <img
          src={mediaApi.serveUrl(item.r2_key)}
          alt={item.filename}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <video
          src={mediaApi.serveUrl(item.r2_key)}
          muted
          preload="metadata"
          className="w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
        <div className="flex justify-end gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onUse(); }}
            title="Use in post"
            className="p-1.5 rounded-md bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete"
            className="p-1.5 rounded-md bg-black/50 text-white hover:bg-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-white truncate">{item.filename}</p>
          {item.size_bytes && (
            <p className="text-xs text-white/70">{formatBytes(item.size_bytes)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MediaRow({
  item,
  onDelete,
  onOpen,
  onUse,
}: {
  item: MediaItem;
  onDelete: () => void;
  onOpen: () => void;
  onUse: () => void;
}) {
  const isImage = item.mime_type?.startsWith('image/');
  return (
    <div
      onClick={onOpen}
      className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-accent/40 transition-colors"
    >
      <div className="h-10 w-10 rounded-md bg-muted overflow-hidden flex items-center justify-center shrink-0">
        {isImage ? (
          <img src={mediaApi.serveUrl(item.r2_key)} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <Video className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{item.filename}</p>
        <p className="text-xs text-muted-foreground">
          {item.mime_type ?? 'Unknown'} {item.size_bytes ? `· ${formatBytes(item.size_bytes)}` : ''}
          {' · '}{formatRelativeTime(item.created_at)}
        </p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onUse(); }}
        title="Use in post"
        className="p-1.5 text-muted-foreground hover:text-accent rounded-md hover:bg-accent/10"
      >
        <Send className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete"
        className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
